import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaCalendar, FaArrowRight, FaThLarge } from 'react-icons/fa';
import { useWordPress } from '../hooks/useWordPress';
import FeaturedCarousel from '../components/FeaturedCarousel';
import { formatHeroPost, formatCardPosts } from '../utils/postFormat';
import { buildCarouselSlides } from '../utils/carousel';
import { FEATURED_TAG_SLUGS, CAROUSEL_SLIDE_COUNT } from '../config/featured';
import './HomePage.css';

// Carousel slides plus the cards below it
const POSTS_TO_FETCH = CAROUSEL_SLIDE_COUNT + 3;

const HomePage = () => {
  const { useRecentPosts, useFeaturedTags, useFeaturedPosts } = useWordPress();
  const { data: recentPostsData, isLoading: postsLoading } = useRecentPosts(POSTS_TO_FETCH);
  const { data: featuredTags, isLoading: tagsLoading } = useFeaturedTags();

  // Featured tag IDs in slot order — featured-1 is slide 2, featured-2 is slide 3
  const tagIdsBySlot = React.useMemo(
    () => FEATURED_TAG_SLUGS
      .map(slug => featuredTags?.find(tag => tag.slug === slug)?.id)
      .filter(Boolean),
    [featuredTags]
  );

  const { data: featuredPosts, isLoading: featuredLoading } = useFeaturedPosts(tagIdsBySlot);

  // Gate the grid on the featured lookup too, so it doesn't render a set of cards
  // that reshuffles the moment the starred posts arrive
  const isLoading = postsLoading || tagsLoading || featuredLoading;

  const recentPosts = recentPostsData?.posts || [];
  const carouselPosts = buildCarouselSlides({
    recentPosts,
    featuredPosts: featuredPosts || [],
    tagIdsBySlot
  });

  const slides = carouselPosts.map(formatHeroPost);

  // Don't repeat a carousel post in the grid below
  const carouselIds = new Set(carouselPosts.map(post => post.id));
  const gridPosts = formatCardPosts(
    recentPosts.filter(post => !carouselIds.has(post.id))
  ).slice(0, 3);

  return (
    <div className="homepage">
      {/* Hero Section - Featured post carousel */}
      <FeaturedCarousel slides={slides} isLoading={isLoading} />

      {/* Recent Blog Posts */}
      <section className="section recent-posts-section">
        <div className="container">
          <div className="section-title">
            <h2>Recent Posts</h2>
            <p>Dive into more stories and insights from my latest adventures</p>
          </div>

          {isLoading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Loading recent posts...</p>
            </div>
          ) : gridPosts.length > 0 ? (
            <>
              <div className="blog-posts-grid">
                {gridPosts.map((post, index) => (
                  <motion.article
                    key={post.id}
                    className="blog-post-card"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <div className="blog-post-card-image">
                      <img src={post.image} alt={post.title} />
                      <div className="blog-post-card-category">
                        {post.category}
                      </div>
                    </div>

                    <div className="blog-post-card-content">
                      <div className="blog-post-card-meta">
                        <span className="meta-date">
                          <FaCalendar />
                          {new Date(post.date).toLocaleDateString()}
                        </span>
                      </div>

                      <h3 className="blog-post-card-title">{post.title}</h3>
                      <p className="blog-post-card-excerpt">{post.excerpt}</p>

                      <Link
                        to={`/blog/${post.slug}`}
                        className="read-more-link"
                      >
                        Read More <FaArrowRight className="read-more-arrow" />
                      </Link>
                    </div>
                  </motion.article>
                ))}
              </div>

              <div className="text-center view-all-wrap">
                <Link to="/blog" className="btn btn-primary btn-view-all">
                  <FaThLarge />
                  View All Posts
                </Link>
              </div>
            </>
          ) : (
            <div className="no-posts">
              <p>No additional posts available.</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default HomePage;
